import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { ChevronDown, ChevronRight, Filter, Eye, EyeOff, AlertTriangle, Copy, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { newSelect as Select } from '../ui/newSelect';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Actions as IMPORT_ACTIONS } from '../../store/actions/intelligentImportActions';
import { STAGING_ROW_STATUSES } from '../../constants/importConstants';

const statusColors = {
  [STAGING_ROW_STATUSES.PENDING_MATCH]: 'bg-info-bg text-info',
  [STAGING_ROW_STATUSES.PENDING_REVIEW]: 'bg-warning-bg text-warning',
  [STAGING_ROW_STATUSES.CONFIRMED]: 'bg-success-bg text-success',
  [STAGING_ROW_STATUSES.DUPLICATE_SKIPPED]: 'bg-muted text-muted-foreground',
  [STAGING_ROW_STATUSES.DEDUP_OVERRIDDEN]: 'bg-warning-bg text-warning',
  [STAGING_ROW_STATUSES.COMMITTED]: 'bg-success-bg text-success',
  [STAGING_ROW_STATUSES.COMMIT_ERROR]: 'bg-danger-bg text-danger',
  [STAGING_ROW_STATUSES.SPLIT]: 'bg-info-bg text-info',
  [STAGING_ROW_STATUSES.UNMATCHED]: 'bg-danger-bg text-danger',
};

const statusFilterOptions = [
  { value: 'all', label: 'All Rows' },
  ...Object.entries(STAGING_ROW_STATUSES).map(([key, val]) => ({
    value: val,
    label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
  })),
];

const StagingTable = ({
  importId,
  job,
  stagingItems,
  filteredRows,
  loading,
  getStagingItems,
  getFilteredRows,
  overrideDedupSkip,
}) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFiltered, setShowFiltered] = useState(false);

  useEffect(() => {
    if (importId) {
      getStagingItems(importId);
      getFilteredRows(importId);
    }
  }, [importId]);

  const getStatus = useCallback((r) => r.pipelineStatus || r.status, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return stagingItems;
    return stagingItems.filter((r) => getStatus(r) === statusFilter);
  }, [stagingItems, statusFilter, getStatus]);

  const stats = useMemo(() => {
    const total = stagingItems.length;
    const dupes = stagingItems.filter((r) => getStatus(r) === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED).length;
    const confirmed = stagingItems.filter((r) => getStatus(r) === STAGING_ROW_STATUSES.CONFIRMED).length;
    const pending = stagingItems.filter((r) =>
      [STAGING_ROW_STATUSES.PENDING_MATCH, STAGING_ROW_STATUSES.PENDING_REVIEW].includes(getStatus(r)),
    ).length;
    return { total, dupes, confirmed, pending, filtered: filteredRows.length };
  }, [stagingItems, filteredRows]);

  const handleDedupOverride = useCallback(
    async (stagingId) => {
      await overrideDedupSkip(importId, stagingId);
      getStagingItems(importId, { silent: true });
    },
    [importId, overrideDedupSkip, getStagingItems],
  );

  if (loading?.staging) {
    return <div className='text-center py-12 text-muted-foreground'>Loading staging data...</div>;
  }

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3'>
        {[
          { label: 'Total Rows', value: stats.total, icon: null },
          { label: 'Pending', value: stats.pending, className: 'text-info' },
          { label: 'Confirmed', value: stats.confirmed, className: 'text-success' },
          { label: 'Duplicates', value: stats.dupes, className: 'text-warning' },
          { label: 'Bank Filtered', value: stats.filtered, className: 'text-muted-foreground' },
        ].map((s) => (
          <Card key={s.label} className='bg-card'>
            <CardContent className='p-4 text-center'>
              <p className='text-2xl font-bold text-foreground'>{s.value}</p>
              <p className={`text-xs ${s.className || 'text-muted-foreground'}`}>{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className='bg-card'>
        <CardHeader className='flex flex-row items-center justify-between'>
          <CardTitle className='text-lg'>Parsed Rows</CardTitle>
          <div className='flex items-center gap-3'>
            <Select
              value={statusFilter}
              onValueChange={setStatusFilter}
              options={statusFilterOptions}
              placeholder='Filter by status'
              className='w-48'
            />
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className='text-right'>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row) => (
                <TableRow key={row.id || row.stagingId}>
                  <TableCell className='text-sm font-mono'>{row.date || '—'}</TableCell>
                  <TableCell className='max-w-[300px] truncate text-sm' title={row.cleanDescription || row.rawDescription || row.description}>
                    {row.cleanDescription || row.rawDescription || row.description}
                  </TableCell>
                  <TableCell className='text-right font-mono text-sm'>
                    {row.currency || '£'}{Number(row.amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Badge className={statusColors[getStatus(row)] || 'bg-muted text-muted-foreground'}>
                      {getStatus(row)?.replace(/_/g, ' ') || '—'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {getStatus(row) === STAGING_ROW_STATUSES.DUPLICATE_SKIPPED && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Button
                              variant='ghost'
                              size='sm'
                              onClick={() => handleDedupOverride(row.id || row.stagingId)}
                            >
                              <Copy size={14} strokeWidth={1.5} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Override duplicate skip</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className='text-center py-8 text-muted-foreground'>
                    No rows match the current filter.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {filteredRows.length > 0 && (
        <Card className='bg-card'>
          <CardHeader>
            <div
              className='flex items-center gap-2 cursor-pointer'
              onClick={() => setShowFiltered(!showFiltered)}
            >
              {showFiltered ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <CardTitle className='text-sm'>
                Bank-Filtered Rows ({filteredRows.length})
              </CardTitle>
              <span className='text-xs text-muted-foreground ml-2'>
                Transactions filtered by bank blocklist (internal transfers, card payments, etc.)
              </span>
            </div>
          </CardHeader>
          {showFiltered && (
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className='text-right'>Amount</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row, i) => (
                    <TableRow key={i} className='opacity-60'>
                      <TableCell className='text-sm font-mono'>{row.date || '—'}</TableCell>
                      <TableCell className='text-sm'>{row.cleanDescription || row.rawDescription || row.description}</TableCell>
                      <TableCell className='text-right font-mono text-sm'>
                        {row.currency || '£'}{Number(row.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className='text-sm text-muted-foreground'>{row.filterReason || 'Blocklist match'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  stagingItems: state.intelligentImport?.stagingItems || [],
  filteredRows: state.intelligentImport?.filteredRows || [],
  loading: state.intelligentImport?.loading || {},
});

const mapDispatchToProps = {
  getStagingItems: IMPORT_ACTIONS.getStagingItems,
  getFilteredRows: IMPORT_ACTIONS.getFilteredRows,
  overrideDedupSkip: IMPORT_ACTIONS.overrideDedupSkip,
};

export default connect(mapStateToProps, mapDispatchToProps)(StagingTable);
